import React from "react";
import PropTypes from "prop-types";
import { TableBody, TableRow, TableCell, Checkbox } from "@mui/material";
import EmptyState from "@/components/molecules/EmptyState/EmptyState";
import ShimmerTableRow from "@/components/atoms/ShimmerTableRow";
import { LinkText, Tag } from "@/components/atoms";
import { COLOR_CODE, TAG_COLOR } from "@/utils/constants/colors";

/**
 * Pure presentational table body for CustomDataTable.
 * All business logic (links, downloads, formatting) must be provided via column config or parent/handler.
 *
 * Example column config usage:
 * {
 *   name_of_db_column: 'salesTargetName',
 *   render: (row, column, rowIndex, colIndex) => <LinkText ... />, // full custom renderer
 *   linkHandler: (row) => { ... }, // for clickable text
 *   linkLabel: (row) => row.salesTargetName, // for clickable text label
 *   downloadHandler: (row) => { ... }, // for download link
 *   downloadLabel: () => 'Download', // for download link label
 * }
 */
const DataTableBody = ({
  isLoading,
  records,
  columns,
  selectedRows,
  handleRowSelect,
  handleResetAll
}) => (
  <TableBody>
    {isLoading && (
      Array.from({ length: 12 }).map((_, index) => (
        <ShimmerTableRow key={`loading-${index}`} columnCount={columns.length + 2} />
      ))
    )}
    {!isLoading && records?.length === 0 && (
      <TableRow className="empty-state-row">
        <TableCell
          colSpan={columns.length + 1}
          align="center"
          sx={{
            backgroundColor: COLOR_CODE._1D1D1D,
            '&:hover': {
              backgroundColor: COLOR_CODE._1D1D1D
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: COLOR_CODE._1D1D1D
            }
          }}
        >
          <EmptyState onActionClick={handleResetAll} />
        </TableCell>
      </TableRow>
    )}
    {!isLoading && records?.length > 0 && records.map((row, rowIndex) => (
      <TableRow
        key={`row-${rowIndex}`}
        selected={selectedRows.some(selectedRow => (selectedRow.id ?? selectedRow.uuid ?? selectedRow.productId) === (row.id ?? row.uuid ?? row.productId))}

      >
        <TableCell sx={{
          width: '60px',
        }}>
          <Checkbox
            size="small"
            className="custom-checkbox"
            checked={selectedRows.some(selectedRow => (selectedRow.id ?? selectedRow.uuid ?? selectedRow.productId) === (row.id ?? row.uuid ?? row.productId))}
            onChange={() => handleRowSelect(row)}
          />
        </TableCell>
        {columns.map((column, colIndex) => (
          <TableCell className="custom-data-table-cell" key={`cell-${colIndex}`} align="left" >
            {typeof column.render === 'function' ? (
              column.render(row, column, rowIndex, colIndex)
            ) : column.linkHandler ? (
              <LinkText
                text={column.linkLabel ? column.linkLabel(row, column, rowIndex, colIndex) : (row[column.name_of_db_column] || '\u2014')}
                id={`link-${rowIndex}-${column.name_of_db_column}`}
                handleClick={() => column.linkHandler(row, column, rowIndex, colIndex)}
              />
            ) : column.downloadHandler ? (
              row.status === 'Draft' ? (
                <div className="text-gray-500">{column.downloadLabel ? column.downloadLabel(row, column, rowIndex, colIndex) : 'Download'}</div>
              ) : (
                <div>
                  <LinkText
                    text={column.downloadLabel ? column.downloadLabel(row, column, rowIndex, colIndex) : 'Download'}
                    id={row?.id}
                    handleClick={() => column.downloadHandler(row, column, rowIndex, colIndex)}
                  />
                </div>
              )
            ) : (
              column.name_of_db_column == 'includeExclude' ?
                <Tag
                  text={row?.included ? 'Exclude' : 'Include'}
                  color={row?.included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150}
                  onClick={() => { }} // Placeholder for actual click handler
                /> :
                <div style={{
                  overflow: 'hidden',
                  width: '100%',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}>
                  {
                    row[column.name_of_db_column] === null || row[column.name_of_db_column] === undefined ? '—' :
                      row[column.name_of_db_column]
                  }
                </div>
            )}
          </TableCell>
        ))}
        <TableCell sx={{
          width: '60px',
        }}>
        </TableCell>
      </TableRow>
    ))}
  </TableBody>
);

DataTableBody.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  records: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  handleRowSelect: PropTypes.func.isRequired,
  handleResetAll: PropTypes.func.isRequired
};

export default DataTableBody;
